import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core';
import { Injector } from '@angular/core';
import moment from 'moment';

import { DatacenterSelectionService } from '../../../services/datacenter-selection.service';
import { FastLaneItems } from '../../../../fastlane/models/fastlane-datacenter-config';
import { FastLaneService } from '../../../../fastlane/services/fastlane.service';
import { Strategy } from '../strategy';
import { OrderBy, OrderByDirection } from '../../../utils/constant';
import { PaginatedResult } from '../../../models/paginated-result';
import { ColDefWithFields, ColumnType } from '../../column-type';
import { CommonGridOptions } from '../../grid-options';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { FilterModel } from '../../../models/ag-grid';
import { DATE_FORMAT_PATTERN_DD_MM_YYYY, FilterDateFormatType,
	WEEKYEAR_FORMAT_PATTERN } from '../../../models/filter-formats';
import { formatTextDimension } from '../../../utils/number.format';

export class StrategyProtoType implements Strategy<FastLaneItems> {
	protoTypeService: FastLaneService;
	selectionService: DatacenterSelectionService;
	defaultOrderBy = new OrderBy<keyof FastLaneItems>('id', OrderByDirection.DESC);
	hasTotal = false;
	aggregateFields = [];

	constructor(injector: Injector) {
		this.protoTypeService = injector.get<FastLaneService>(FastLaneService);
		this.selectionService = injector.get<DatacenterSelectionService>(DatacenterSelectionService);
	}

	setTotal(_api?: GridApi, _filter?: string): Subscription {
		throw new Error('Method not implemented.');
	}

	async getAllDataSelected(
		currentColumns: string[],
		filter: string | undefined,
		orderBy: OrderBy<keyof FastLaneItems>[],
	) {
		return this.getAllData(orderBy, currentColumns, filter)
			.then((organizations: PaginatedResult<FastLaneItems>) => this.removeDeselectedRows(organizations));
	}

	async getAllData(
		orderBy: OrderBy<keyof FastLaneItems>[],
		currentColumns: string[],
		filter: string | undefined
	) {
		return firstValueFrom(this.protoTypeService.getFastLaneItems(
			<(keyof FastLaneItems)[]> currentColumns,
			filter,
			0,
			60_000,
			orderBy,
			false
		));
	}

	formatCellByType(columnDef: ColDefWithFields<FastLaneItems>, data: FastLaneItems) {
		const field = columnDef.field || '';
		const fields = columnDef.fields || [];
		const { columnType } = columnDef;
		switch (columnType) {
		case ColumnType.DIMENSION_COLUMN:
			return formatTextDimension(
					<string>data[<keyof FastLaneItems>fields[0]],
					<string>data[<keyof FastLaneItems>fields[1]],
					<string>data[<keyof FastLaneItems>fields[2]]
			);
		case ColumnType.NUMBER_COLUMN:
		{
			const value = <number | undefined>data[<keyof FastLaneItems>field];
			return value === undefined ? '' : String(value);
		}
		case ColumnType.TEXT_COLUMN:
		{
			const textValue = <string | undefined>data[<keyof FastLaneItems>field];
			return textValue === undefined ? '' : String(textValue);
		}
		case ColumnType.TRANSLATED_COLUMN:
		{
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			const textValue = <string | undefined>data[<keyof FastLaneItems>columnDef.filterParams.fastlaneFields[1]];
			return textValue === undefined ? '' : String(textValue);
		}
		case ColumnType.DATE_COLUMN:
			if (data[<keyof FastLaneItems>field]) {
				const dateformat = (<FilterDateFormatType>columnDef.cellRendererParams).outputFormat === 'week'
					? WEEKYEAR_FORMAT_PATTERN : DATE_FORMAT_PATTERN_DD_MM_YYYY;
				const weekData = dateformat === WEEKYEAR_FORMAT_PATTERN && data[<keyof FastLaneItems> field]
					=== '1900-W01' ? '' : data[<keyof FastLaneItems> field];
				return weekData === '' ? '' : moment.utc(<string> weekData).format(dateformat);
			}
			return '';
		default:
			throw new Error('unknown filter type');
		}
	}

	getCustomGridOptions(defaultGridOptions: CommonGridOptions) {
		return new CommonGridOptions<FastLaneItems>({
			...defaultGridOptions,
			getRowId: ({ data }: { data: FastLaneItems }) => String(data.id)
		});
	}

	addIdColumn(currentColumns: (keyof FastLaneItems)[]) {
		if (!currentColumns.includes('id')) {
			currentColumns.push('id');
		}
		return currentColumns;
	}

	getDataFromGrid(
		orderBy: OrderBy<keyof FastLaneItems>[],
		currentColumns: (keyof FastLaneItems)[],
		filter: string | undefined,
		params: IServerSideGetRowsParams<FastLaneItems>,
		rowsCountSubject: BehaviorSubject<number | undefined>
	) {
		const currentColumnsWithId = this.addIdColumn(currentColumns);
		return this.protoTypeService.getFastLaneItems(
			currentColumnsWithId,
			filter,
			params.request.startRow ?? 0,
			250,
			orderBy,
			false
		).subscribe({
			next: res => {
				const lastRow = params.request.endRow === undefined || res.count <= params.request.endRow
					? res.count
					: -1;
				params.success({ rowData: res.results, rowCount: lastRow });
				rowsCountSubject.next(res.count);
			},
			error: (err: unknown) => {
				console.error('error requesting fastlane datacenter', err);
				rowsCountSubject.next(undefined);
				params.fail();
			}
		});
	}

	createAggregateCustomViewFilter(_view: UserDatacenterView): string {
		throw new Error('Method not implemented.');
	}

	createAggregateQuickSearchFilter(
		_currentSelectedView: UserDatacenterView,
		_api?: GridApi,
		_filterModel?: FilterModel
	): string {
		throw new Error('Method not implemented.');
	}

	private removeDeselectedRows(fastlaneItems: PaginatedResult<FastLaneItems>) {
		return fastlaneItems.results.filter(batch => {
			const rowDeselected = this.selectionService.findDeselectedNode(String(batch.id));
			return !rowDeselected;
		});
	}
}
